import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, F as FormController } from './form.js';
import { B as BusinessFormStep } from './business-form-types.js';
import { g as bankNameValidation, n as nicknameValidation, i as identityNameValidation, h as accountTypeValidation, j as accountNumberValidation, k as routingNumberValidation } from './schema-validations.js';
import { c as bankAccountTypeOptions } from './business-form-options.js';
import { A as Api } from './Api.js';
import './Pagination.js';
import './Payment.js';
import './Payout.js';
import { c as config } from './config.js';
import { n as numberOnlyHandler } from './utils4.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { d as defineCustomElement$4 } from './form-control-error-text2.js';
import { d as defineCustomElement$3 } from './form-control-help-text2.js';
import { d as defineCustomElement$2 } from './form-control-select2.js';
import { d as defineCustomElement$1 } from './form-control-text2.js';

const businessBankAccountSchema = (allowOptionalFields) => {
    const schema = create$3({
        bank_name: bankNameValidation.required('Enter bank name'),
        nickname: nicknameValidation.required('Enter nickname'),
        account_owner_name: identityNameValidation.required('Enter account owner name'),
        account_type: accountTypeValidation.required('Select account type'),
        account_number: accountNumberValidation.required('Enter account number'),
        routing_number: routingNumberValidation.required('Enter routing number')
    });
    const easySchema = create$3({
        bank_name: bankNameValidation.nullable(),
        nickname: nicknameValidation.nullable(),
        account_owner_name: identityNameValidation.nullable(),
        account_type: accountTypeValidation.nullable(),
        account_number: accountNumberValidation.nullable(),
        routing_number: routingNumberValidation.nullable()
    });
    return allowOptionalFields ? easySchema : schema;
};

class BankAccount {
    constructor(data) {
        this.id = data.id;
        this.account_owner_name = data.account_owner_name;
        this.full_name = data.full_name;
        this.account_type = data.account_type;
        this.acct_last_four = data.acct_last_four;
        this.account_number_last4 = data.account_number_last4;
        this.routing_number = data.routing_number;
        this.bank_name = data.bank_name;
        this.country = data.country;
        this.currency = data.currency;
        this.nickname = data.nickname;
        this.metadata = data.metadata;
        this.business_id = data.business_id;
        this.platform_account_id = data.platform_account_id;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
    }
}

const BusinessBankAccountFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessBankAccountFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.fetchData = async () => {
            this.formLoading.emit(true);
            try {
                const response = await this.api.get(this.businessEndpoint);
                if (response.data.bank_accounts.length > 0) {
                    this.bankAccount = Object.assign({}, new BankAccount(response.data.bank_accounts[0]));
                }
                else {
                    this.bankAccount = Object.assign({}, new BankAccount({}));
                }
                this.formController.setInitialValues(Object.assign({}, this.bankAccount));
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.sendData = async (onSuccess) => {
            this.formLoading.emit(true);
            try {
                const formValues = this.formController.values.getValue();
                const payload = Object.assign(Object.assign({}, formValues), { business_id: this.businessId });
                const response = await this.api.post(this.bankAccountEndpoint, payload);
                this.handleResponse(response, onSuccess);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.bankAccount = undefined;
        this.authToken = undefined;
        this.businessId = undefined;
        this.allowOptionalFields = undefined;
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get bankAccountEndpoint() {
        return `entities/bank_accounts`;
    }
    get formDisabled() {
        var _a;
        return !!((_a = this.bankAccount) === null || _a === void 0 ? void 0 : _a.id);
    }
    handleResponse(response, onSuccess) {
        if (response.error) {
            this.errorEvent.emit({
                errorCode: ComponentErrorCodes.POST_ERROR,
                message: response.error.message,
                severity: ComponentErrorSeverity.ERROR,
                data: response.error,
            });
        }
        else {
            onSuccess();
        }
        this.submitted.emit({ data: response, metadata: { completedStep: BusinessFormStep.bankAccount } });
    }
    async validateAndSubmit({ onSuccess }) {
        this.formDisabled ? onSuccess() :
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
    }
    ;
    componentWillLoad() {
        this.formController = new FormController(businessBankAccountSchema(this.allowOptionalFields));
        this.api = Api({ authToken: this.authToken, apiOrigin: config.proxyApiOrigin });
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    componentDidLoad() {
        this.formController.values.subscribe(values => this.bankAccount = Object.assign({}, values));
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        const bankAccountDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: 'c2bdcd5ab6301d1a39e4d774df101281c41e2002' }, h("fieldset", { key: '7f6b607be5b7bc5fcd0dc1687adaa1ad00d7d2e9' }, h("legend", { key: '91b68746c65bb68f4fbd2eef151258e6915f8e35' }, "Bank Account Info"), h("hr", { key: '5eceedbf59faef10757c9e2b04d4dc4cd6b88a9b' }), h("div", { key: 'd3185db7fceb9851ccb44c1c89f8cbe5a37c24e0', class: "row gy-3" }, h("div", { key: '625caf0e9b93c244d94c69174ffff57126d39fa1', class: "col-12" }, h("form-control-text", { key: 'ddf3206b09047dfea56df850babe6c8f92ed33ee', name: "bank_name", label: "Bank Name", defaultValue: bankAccountDefaultValue.bank_name, errorText: this.errors.bank_name, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: 'd7d498353eec8e1947ce748aa35cbee63e793817', class: "col-12" }, h("form-control-text", { key: 'a80050dcd088db0209c2e5da65b310d7fc7877f5', name: "nickname", label: "Nickname", defaultValue: bankAccountDefaultValue.nickname, errorText: this.errors.nickname, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: 'ea5578470e0e4d0fd5b973807475a02c7764c9c8', class: "col-12" }, h("form-control-text", { key: 'dd8c77b43ddfca61cfd5ed39ce6e142654f18d9f', name: "account_owner_name", label: "Account Owner Name", defaultValue: bankAccountDefaultValue.account_owner_name, errorText: this.errors.account_owner_name, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: 'cb3256226c5fdfa5ff2a02c456a6c384e06d16a6', class: "col-12" }, h("form-control-select", { key: 'a865f48350a0ae6b6a2a540dc2d54de72a8af074', name: "account_type", label: "Account Type", options: bankAccountTypeOptions, defaultValue: bankAccountDefaultValue.account_type, errorText: this.errors.account_type, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: 'aa9062a25dade06db1f175d4233f2e5bb4e7ecd7', class: "col-12" }, h("form-control-text", { key: '1a87920e4df50178625446c770e4182d918664c7', name: "account_number", label: "Account Number", defaultValue: bankAccountDefaultValue.account_number, maxLength: 17, errorText: this.errors.account_number, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, disabled: this.formDisabled })), h("div", { key: 'c72858a29aa1289b367c0eb89d6cc0eded23346d', class: "col-12" }, h("form-control-text", { key: 'b7fb93bcf16c01689245d5aca11142b013fe6adc', name: "routing_number", label: "Routing Number", defaultValue: bankAccountDefaultValue.routing_number, maxLength: 9, errorText: this.errors.routing_number, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, disabled: this.formDisabled }))))));
    }
}, [0, "justifi-business-bank-account-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "bankAccount": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-bank-account-form-step", "form-control-error-text", "form-control-help-text", "form-control-select", "form-control-text"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-bank-account-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessBankAccountFormStep);
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessBankAccountFormStep as B, defineCustomElement as d };
